import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { Award } from "lucide-react";
import { db } from "../../firebase";
import { CFR_CATEGORIES } from "../../constants/cfrCategories";
import "./BrowseCauses.css";


function normalizeCategories(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function categoryName(cat) {
  return cat.label || cat.name || cat.title || cat.value;
}

function categoryDescription(cat) {
  return cat.description || cat.summary || "Explore local organizations and needs connected to this community focus area.";
}

function formatCurrency(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

export default function CauseDetail() {
  const { causeId } = useParams();
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [loading, setLoading] = useState(true);

  const cause = useMemo(
    () => CFR_CATEGORIES.find(c => c.value === causeId),
    [causeId]
  );

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const [orgSnap, needSnap] = await Promise.all([
          getDocs(collection(db, "organizations")),
          getDocs(collection(db, "needs")),
        ]);

        setOrganizations(orgSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setNeeds(needSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("CauseDetail load error", err);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  const matchingOrgs = useMemo(() => {
    if (!cause) return [];
    return organizations.filter(org =>
      normalizeCategories(org.cfrCategories).includes(cause.value)
    );
  }, [organizations, cause]);

  const matchingNeeds = useMemo(() => {
    if (!cause) return [];
    return needs.filter(need =>
      normalizeCategories(need.cfrCategories).includes(cause.value)
    );
  }, [needs, cause]);

  const openNeeds = useMemo(() => {
    return matchingNeeds.filter(need =>
      ["open", "available", "active"].includes((need.status || "open").toLowerCase())
    );
  }, [matchingNeeds]);

  const fundedNeeds = useMemo(() => {
    return matchingNeeds.filter(need =>
      ["funded", "fulfilled", "completed", "sponsored"].includes((need.status || "").toLowerCase())
    );
  }, [matchingNeeds]);

  if (!cause) {
    return (
      <div className="causes-page">
        <button className="causes-back" onClick={() => navigate("/give/causes")}>
          ← All causes
        </button>
        <h1>Cause not found</h1>
      </div>
    );
  }

  return (
    <div className="causes-page">
      <header className="causes-header">
        <div>
          <button className="causes-back" onClick={() => navigate("/give/causes")}>
            ← All causes
          </button>
          <h1>{categoryName(cause)}</h1>
          <p>{categoryDescription(cause)}</p>
        </div>

        <button className="causes-secondary" onClick={() => navigate("/give/browse")}>
          Browse all needs
        </button>
      </header>

      <section className="cause-detail-hero">
        <div className="cause-detail-icon">{cause.icon || "◎"}</div>
        <div>
          <div className="causes-kicker">Community Focus Area</div>
          <h2>{categoryName(cause)}</h2>
          <p>{categoryDescription(cause)}</p>
        </div>
      </section>

      {loading ? (
        <div className="causes-loading">Loading cause...</div>
      ) : (
        <>
          <section className="cause-summary-grid">
            <div>
              <strong>{matchingOrgs.length}</strong>
              <span>participating organizations</span>
            </div>
            <div>
              <strong>{openNeeds.length}</strong>
              <span>open needs</span>
            </div>
            <div>
              <strong>{fundedNeeds.length}</strong>
              <span>funded needs</span>
            </div>
            <div>
              <strong>{matchingNeeds.length}</strong>
              <span>total linked needs</span>
            </div>
          </section>

          {(cause.redFlags?.length > 0 || cause.blueRibbons?.length > 0) && (
            <section className="cause-cfr-section">
              <div className="cause-cfr-column">
                <h3 className="cfr-red-title">🚩 Red Flags</h3>
                {cause.redFlags?.length > 0 ? (
                  <ul>
                    {cause.redFlags.map(flag => (
                      <li key={flag}>{flag}</li>
                    ))}
                  </ul>
                ) : (
                  <p>No official Red Flags stored for this category yet.</p>
                )}
              </div>

              <div className="cause-cfr-column">
                <h3 className="cfr-blue-title">
                  <Award size={30} strokeWidth={2.4} />
                  Blue Ribbons
                </h3>
                {cause.blueRibbons?.length > 0 ? (
                  <ul>
                    {cause.blueRibbons.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p>No official Blue Ribbons stored for this category yet.</p>
                )}
              </div>
            </section>
          )}

          <section className="cause-platform-section">
            <h2>How SomebodyGives Helps</h2>
            <p>
              Local nonprofits are addressing this community priority through
              specific, tangible needs that donors can fund.
            </p>
          </section>

          <section className="cause-section">
            <div className="cause-section-header">
              <h2>Ways to help right now</h2>
              <button onClick={() => navigate("/give/browse")}>
                Browse all needs →
              </button>
            </div>

            {openNeeds.length === 0 ? (
              <div className="cause-empty">
                No open needs are currently linked to this cause.
              </div>
            ) : (
              <div className="cause-list">
                {openNeeds.slice(0, 8).map(need => (
                  <article key={need.id} className="cause-need-card">
                    <div>
                      <div className="cause-card-org">
                        {need.orgName || need.organizationName || "Local organization"}
                      </div>
                      <h3>{need.itemName || need.title || need.name || "Need"}</h3>
                      <p>
                        {need.description ||
                          need.impactStatement ||
                          "Specific support requested by a local organization."}
                      </p>
                    </div>

                    <div className="cause-need-side">
                      <div className="cause-price">
                        {formatCurrency(need.pricePerCycle || need.cost || need.baseCost)}
                      </div>
                      <button onClick={() => navigate(`/give/adopt/${need.id}`)}>
  Support this need →
</button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="cause-section">
            <div className="cause-section-header">
              <h2>Organizations working in this area</h2>
              <button onClick={() => navigate("/give/orgs")}>
                Browse all orgs →
              </button>
            </div>

            {matchingOrgs.length === 0 ? (
              <div className="cause-empty">
                No organizations are currently linked to this cause.
              </div>
            ) : (
              <div className="cause-org-grid">
                {matchingOrgs.map(org => (
                  <article key={org.id} className="cause-org-card">
                    <div className="cause-org-logo">
                      {org.logoUrl || org.logoURL || org.logo ? (
                        <img src={org.logoUrl || org.logoURL || org.logo} alt="" />
                      ) : (
                        <span>🏢</span>
                      )}
                    </div>

                    <div>
                      <h3>
                        {org.name || org.orgName || org.organizationName || "Organization"}
                      </h3>
                      <p>
                        {org.mission ||
                          org.description ||
                          "Local organization connected to this community focus area."}
                      </p>
                      <button onClick={() => navigate(`/o/${org.id}`)}>
                        View organization →
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}